/**
 * EmptyStatePrompt
 *
 * Full-block empty state used wherever a page can't render because no
 * live integration is connected and demo mode isn't active. Pairs a
 * "Connect QuickBooks" CTA with a smaller "Load Demo Data" secondary
 * action so someone giving a walkthrough (e.g. Gary) can seed the
 * demo dataset in one click and un-do it just as easily later.
 *
 * Palette: Blue = active/live actions, Amber = secondary (demo), no
 * red/green anywhere per compliance rules.
 */
import { Link2, PlayCircle, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useDemoMode } from "../../hooks/useDemoMode";

export function EmptyStatePrompt({
  title = "No live financial data yet",
  description = "Connect QuickBooks to pull your real numbers, or load the demo dataset to walk through the dashboard.",
  connectTo = "/integrations",
  showDemo = true,
  onDemoSeeded,
  testId = "empty-state-prompt",
}) {
  const { status, busy, seed } = useDemoMode();
  const [errorMsg, setErrorMsg] = useState(null);

  const handleSeed = async () => {
    setErrorMsg(null);
    const res = await seed();
    if (!res.ok) {
      setErrorMsg(res.error === "seed_failed" ? "Could not enable demo mode." : String(res.error));
      return;
    }
    if (onDemoSeeded) onDemoSeeded();
    else window.location.reload();
  };

  return (
    <div
      data-testid={testId}
      className="rounded-2xl px-8 py-10 flex flex-col items-center text-center"
      style={{
        background: "linear-gradient(180deg, #F4F6FB 0%, #FFFFFF 100%)",
        border: "1px dashed #C7D2E4",
      }}
    >
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
        style={{ background: "#EFF6FF", border: "1px solid #1B75BA" }}
      >
        <Link2 size={24} style={{ color: "#1B75BA" }} />
      </div>
      <h3 className="text-lg font-bold" style={{ color: "#1C2040" }}>{title}</h3>
      <p className="text-sm mt-2 max-w-md" style={{ color: "#6B7491" }}>{description}</p>

      <div className="flex flex-col sm:flex-row items-center gap-3 mt-6">
        <Link
          to={connectTo}
          data-testid={`${testId}-connect-btn`}
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-transform hover:scale-[1.02]"
          style={{
            background: "linear-gradient(135deg, #282560, #1B75BA)",
            color: "#FFFFFF",
            boxShadow: "0 4px 14px rgba(27,117,186,0.25)",
          }}
        >
          <Link2 size={14} />
          Connect QuickBooks
        </Link>

        {showDemo && !status.quickbooks_live && (
          <button
            type="button"
            data-testid={`${testId}-seed-demo-btn`}
            onClick={handleSeed}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              background: "#FFFFFF",
              color: "#8A6D0B",
              border: "1px solid #D4A017",
            }}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />}
            {busy ? "Seeding..." : "Load Demo Data"}
          </button>
        )}
      </div>

      {errorMsg && (
        <p
          data-testid={`${testId}-error`}
          className="text-xs mt-3"
          style={{ color: "#EA580C" }}
        >
          {errorMsg}
        </p>
      )}
    </div>
  );
}

export default EmptyStatePrompt;
